export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface HttpClientOptions {
  baseUrl: string;
  fetch?: FetchLike;
  defaultHeaders?: Record<string, string>;
}

function getFetchFn(fetchOverride?: FetchLike): FetchLike {
  if (fetchOverride) {
    if (typeof window !== 'undefined' && fetchOverride === window.fetch) {
      return window.fetch.bind(window);
    }

    return fetchOverride;
  }

  return globalThis.fetch.bind(globalThis);
}

export class HttpError extends Error {
  readonly status: number;
  readonly bodyText?: string;

  constructor(message: string, status: number, bodyText?: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly fetchFn: FetchLike;
  private readonly defaultHeaders: Record<string, string>;

  constructor(options: HttpClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.fetchFn = getFetchFn(options.fetch);
    this.defaultHeaders = options.defaultHeaders ?? {};
  }

  async request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    options?: {
      query?: Record<string, string | number | boolean | undefined>;
      headers?: Record<string, string | undefined>;
      bearerToken?: string;
      /** Anonymous device id, used when there is no `bearerToken` (no logged-in user). */
      anonId?: string;
      body?: unknown;
    }
  ): Promise<T> {
    const url = this.buildUrl(path, options?.query);

    const headers: Record<string, string> = this.mergeHeaders(
      this.defaultHeaders,
      options?.headers
    );

    if (options?.bearerToken) {
      headers.Authorization = `Bearer ${options.bearerToken}`;
    } else if (options?.anonId) {
      headers['x-looplay-anon-id'] = options.anonId;
    }

    let body: BodyInit | undefined;
    if (options?.body !== undefined) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
      body = JSON.stringify(options.body);
    }

    const res = await this.fetchFn(url, { method, headers, body });

    if (!res.ok) {
      const text = await this.safeText(res);
      throw new HttpError(`HTTP ${res.status} ${res.statusText}`, res.status, text);
    }

    // Some endpoints may return empty body.
    const text = await this.safeText(res);
    if (!text) return undefined as T;

    return JSON.parse(text) as T;
  }

  private mergeHeaders(
    ...sources: Array<Record<string, string | undefined> | undefined>
  ): Record<string, string> {
    const result: Record<string, string> = {};

    for (const src of sources) {
      if (!src) continue;
      for (const [key, value] of Object.entries(src)) {
        if (value === undefined) continue;
        result[key] = value;
      }
    }

    return result;
  }

  private buildUrl(
    path: string,
    query?: Record<string, string | number | boolean | undefined>
  ): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${this.baseUrl}${normalizedPath}`);

    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value === undefined) continue;
        url.searchParams.set(key, String(value));
      }
    }

    return url.toString();
  }

  private async safeText(res: Response): Promise<string> {
    try {
      return await res.text();
    } catch {
      return '';
    }
  }
}
